/**
 * PoliticaAuditoriaGate — Modal bloqueante que se muestra al usuario la
 * primera vez que entra al sistema, informándole que todas sus operaciones
 * quedan registradas.
 *
 * Origen: incidente del 28-jul-2026, en que alguien intentó vaciar el
 * catálogo desde la UI. Como parte de los blindajes, cada usuario debe
 * aceptar explícitamente esta política.
 *
 * Comportamiento:
 *   - Al montar, llama a GET /auth/me. Si el user no tiene
 *     `politica_auditoria_aceptada_at`, abre el modal bloqueante.
 *   - El modal NO se puede cerrar hasta que marquen el checkbox y hagan
 *     clic en "Aceptar y continuar".
 *   - Al aceptar, POST /auth/aceptar-politica-auditoria. El backend estampa
 *     el timestamp y registra la aceptación en auditoría.
 *
 * Se monta una única vez desde Layout, envolviendo el contenido de la app.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Typography, Button,
  Checkbox, FormControlLabel, Box, Alert, CircularProgress,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import ShieldIcon from '@mui/icons-material/Shield';
import { getAuth } from 'firebase/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

// Firebase entrega el ID token en el momento (no está en localStorage).
// Este es el patrón real de auth del sistema para endpoints que exigen usuario.
async function getFirebaseAuthHeaders() {
  const user = getAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export default function PoliticaAuditoriaGate({ children }) {
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    let cancel = false;
    // Firebase puede tardar 1-2s en tener el user disponible tras el login.
    // Reintentamos 3 veces con 700ms entre intentos.
    const check = async (intento = 0) => {
      if (!getAuth().currentUser) {
        if (intento < 3) return setTimeout(() => check(intento + 1), 700);
        return; // sin sesión — nada que verificar
      }
      try {
        const headers = await getFirebaseAuthHeaders();
        const r = await fetch(`${API_BASE}/auth/me`, { headers });
        if (!r.ok) return;
        const data = await r.json();
        if (cancel) return;
        setUserEmail(data.email || '');
        if (!data.politica_auditoria_aceptada_at) setOpen(true);
      } catch { /* silencio: si falla no bloqueamos la app */ }
    };
    check();
    return () => { cancel = true; };
  }, []);

  const handleAccept = async () => {
    setSaving(true);
    setError('');
    try {
      const headers = await getFirebaseAuthHeaders();
      const r = await fetch(`${API_BASE}/auth/aceptar-politica-auditoria`, {
        method: 'POST', headers,
      });
      if (!r.ok) throw new Error('No se pudo registrar la aceptación. Intenta de nuevo.');
      setOpen(false);
    } catch (e) {
      setError(e.message || 'Error inesperado');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {children}
      <Dialog open={open}
        // Sin `onClose` — no se puede cerrar hasta aceptar
        disableEscapeKeyDown
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box sx={{
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
          color: '#fff', p: 3, display: 'flex', alignItems: 'center', gap: 2,
        }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '50%',
            bgcolor: 'rgba(251, 191, 36, 0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <GavelIcon sx={{ fontSize: 28, color: '#fbbf24' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 800 }}>
              Política de auditoría
            </Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
              Lectura obligatoria antes de continuar
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontSize: 14, color: '#334155', lineHeight: 1.6, mb: 2 }}>
            Este sistema registra <strong>todas</strong> las operaciones que realizas.
            Cada acción — crear una reserva, aprobar un voucher, editar un paquete,
            cambiar una comisión, desactivar un ítem, exportar un reporte —
            queda grabada de forma permanente con:
          </Typography>

          <Box sx={{
            bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2,
            p: 2, mb: 2, display: 'grid', gap: 0.75,
          }}>
            <Row label="Usuario"    value={userEmail || 'tu cuenta'} />
            <Row label="Fecha y hora exacta" value="con precisión de milisegundos" />
            <Row label="Dirección IP" value="desde donde te conectas" />
            <Row label="Detalle de la operación" value="qué cambió, valores anteriores y nuevos" />
          </Box>

          <Alert severity="info" icon={<ShieldIcon />} sx={{ fontSize: 13, mb: 1 }}>
            Puedes consultar tu propia actividad en cualquier momento en el menú
            <strong> Gestión → Mi Actividad</strong>. Los administradores pueden
            revisar la actividad de cualquier usuario cuando hay un incidente.
          </Alert>

          <Typography sx={{ fontSize: 12.5, color: '#64748b', mt: 2 }}>
            Esta política protege tanto a la empresa como a ti mismo:
            deja evidencia clara de las buenas prácticas de cada persona,
            y permite investigar cualquier operación sospechosa.
          </Typography>

          {error && <Alert severity="error" sx={{ mt: 2, fontSize: 13 }}>{error}</Alert>}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
          <FormControlLabel
            control={<Checkbox checked={checked} onChange={e => setChecked(e.target.checked)}
                      sx={{ color: '#1e3a5f', '&.Mui-checked': { color: '#1e3a5f' } }} />}
            label={<Typography sx={{ fontSize: 13, color: '#334155' }}>
              He leído y entiendo que todas mis acciones en el sistema
              quedan registradas de forma permanente.
            </Typography>}
          />
          <Button
            variant="contained" fullWidth
            disabled={!checked || saving}
            onClick={handleAccept}
            sx={{
              background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
              color: '#fff', fontWeight: 700, textTransform: 'none', py: 1.25,
              '&.Mui-disabled': { background: '#cbd5e1', color: '#fff' },
            }}>
            {saving ? <><CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} /> Registrando…</>
                    : 'Aceptar y continuar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function Row({ label, value }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1e3a5f', minWidth: 150 }}>
        {label}:
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: '#475569' }}>{value}</Typography>
    </Box>
  );
}
